import { jsx } from '@amazon/vinyl-tsx'
import { type MutableValue } from '@amazon/vinyl-observable'
import { ctx } from '@/util/context'
import { Icon } from '@/components/Icon'
import {
    type LikeState,
    readTrackLikeOverlay,
    setTrackLikeState,
    type TrackLikesContext,
    whenTrackLikeOverlayHydrated,
} from '@/track/trackLikeService'
import type { Maybe } from '@amazon/vinyl-util'
import { toast } from '@/components/toast'
import { navigateTo } from '@/router/router'
import { signedIn } from '@/auth/authRef'
import type { IconName } from '@/components/Icons'

export interface TrackLikeProps {
    readonly data: MutableValue<Maybe<TrackLikesContext>>
}

export function TrackLikeButton(props: TrackLikeProps) {
    const { errorHandler } = ctx('TrackLikeButton')
    const liked = props.data.pick('likesContext').pick('liked')
    return (
        <button
            className="iconButton trackLikeButton"
            visible={signedIn()}
            onConnect={() => {
                // Apply any persisted overlay state once the data
                // observable settles on a track. Without this, a fresh
                // navigation to a page can render the server's stale
                // `liked` flag for a beat before the user clicks.
                let appliedFor: string | null = null
                const apply = () => {
                    const v = props.data.value
                    if (!v?.id || appliedFor === v.id) return
                    const override = readTrackLikeOverlay(v.id)
                    appliedFor = v.id
                    if (override !== undefined && v.likesContext?.liked !== override) {
                        // `liked` is a writable projection of `props.data`,
                        // so assigning here triggers a notification on the
                        // parent. Defer the write so we don't write into
                        // an observable from inside its own onData
                        // dispatch — the playlist's VirtualList listens
                        // to the same chain and would re-enter.
                        queueMicrotask(() => {
                            liked.value = override
                        })
                    }
                }
                whenTrackLikeOverlayHydrated()
                    .then(apply)
                    .catch(() => {})
                return props.data.onData(apply)
            }}
            onclick={() => {
                const v = props.data.value
                if (!v?.id) return
                const newLiked = !v.likesContext?.liked
                const likeState: LikeState = newLiked ? 'LIKE' : 'DISLIKE'
                liked.value = newLiked
                toast(
                    newLiked ? (
                        <div
                            className="message linkMessage"
                            onclick={() => navigateTo('/recently/added')}
                        >
                            <div>Track added to liked songs</div>
                            <Icon icon={'caretRight'} />
                        </div>
                    ) : (
                        'Track removed from liked songs'
                    )
                )
                setTrackLikeState({
                    trackId: v.id,
                    likeState,
                }).catch((error) => {
                    errorHandler(error)
                    liked.value = !newLiked
                })
            }}
        >
            <Icon
                icon={liked.map((v): IconName => {
                    return v ? 'favoriteOn' : 'favoriteOff'
                })}
            />
        </button>
    )
}
